import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { ContactForm } from "../components/ContactForm";
import { site } from "../lib/site";
import { breadcrumbJsonLd, localBusinessJsonLd } from "../lib/structured-data";
import { IconClock, IconMail, IconPhone, IconPin } from "../components/Icon";

export function Contact() {
  return (
    <>
      <SEO
        title="Contact & devis gratuit - Agent d'entretien à Vichy"
        description="Demandez votre devis gratuit pour un nettoyage professionnel à Vichy ou dans l'Allier. Réponse sous 24 h ouvrées, par téléphone, email ou formulaire."
        path="/contact"
        jsonLd={[
          localBusinessJsonLd(),
          breadcrumbJsonLd([
            { name: "Accueil", url: `${site.baseUrl}/` },
            { name: "Contact", url: `${site.baseUrl}/contact` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: `Contact - ${site.name}`,
            url: `${site.baseUrl}/contact`,
          },
        ]}
      />
      <PageHeader
        eyebrow="Contact"
        title="Parlons de vos besoins d'entretien à Vichy"
        subtitle="Un échange de quelques minutes suffit pour cadrer votre demande. Nous vous envoyons un devis personnalisé sous 48 heures ouvrées."
        breadcrumbs={[{ label: "Accueil", to: "/" }, { label: "Contact" }]}
      />

      <section className="section bg-white">
        <div className="container-page grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="rounded-3xl bg-surface-soft border border-slate-200 p-6 sm:p-8">
              <h2 className="text-2xl">Demander un devis gratuit</h2>
              <p className="mt-2 text-ink-muted">
                Plus votre demande est précise, plus notre réponse sera rapide.
              </p>
              <div className="mt-6">
                <ContactForm />
              </div>
            </div>
          </div>

          <aside className="lg:col-span-5 order-1 lg:order-2 space-y-4">
            <InfoCard
              icon={IconPhone}
              title="Par téléphone"
              value={site.phoneDisplay}
              href={site.phoneHref}
              hint="Du lundi au vendredi, 7h–19h"
            />
            <InfoCard
              icon={IconMail}
              title="Par email"
              value={site.email}
              href={site.emailHref}
              hint="Réponse sous 24 h ouvrées"
            />
            <InfoCard
              icon={IconPin}
              title="Zone d'intervention"
              value={`${site.address.city} et ${site.radiusKm} km autour`}
              hint="Allier (03)"
            />
            <InfoCard
              icon={IconClock}
              title="Horaires"
              value={site.hours}
              hint="Interventions 7j/7 sur rendez-vous"
            />

            <div className="rounded-2xl bg-brand-50 border border-brand-100 p-6">
              <h3 className="text-lg">Urgences & chantier</h3>
              <p className="mt-2 text-sm text-ink-muted">
                Pour un nettoyage fin de chantier, une remise en état rapide ou une intervention
                suite à un sinistre, appelez-nous directement&nbsp;: nous étudions toute demande en
                24 h.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function InfoCard({
  icon: Icon,
  title,
  value,
  href,
  hint,
}: {
  icon: typeof IconPhone;
  title: string;
  value: string;
  href?: string;
  hint?: string;
}) {
  const body = (
    <div className="flex items-start gap-4">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-600 text-white flex-none">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="text-xs uppercase tracking-wider text-ink-muted">{title}</div>
        <div className="mt-0.5 font-semibold text-brand-900">{value}</div>
        {hint && <div className="mt-1 text-xs text-ink-muted">{hint}</div>}
      </div>
    </div>
  );
  return (
    <div className={`card card-hover ${href ? "cursor-pointer" : ""}`}>
      {href ? (
        <a
          href={href}
          className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        >
          {body}
        </a>
      ) : (
        body
      )}
    </div>
  );
}
